import { useMemo } from "react";
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { BaseWidget } from "./BaseWidget";
import { SelectControl } from "../controls/SelectControl";
import { TextControl } from "../controls/TextControl";
import { FirestoreSelectControl } from "../controls/FirestoreSelectControl";
import useSettings from "../../../../hooks/Settings";
import MetricVisualization from "../../../dataVisualizations/MetricVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";
import { PERSONA_STATUS_SHORT_OPTIONS } from "../../../../helpers/personas/personaStatusLabels";
import { recordInLocation } from '../../../../helpers/locations/locationFilter'
import usePersonaLocations from '../../../../hooks/usePersonaLocations';

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// PersonasTotalWidget — KPI big-number widget for the Personas module.
// Counts `Personas` filtered by status + location.


/**
 * @param {Array<{Status?: string, LocationID?: string}>} personas
 * @param {string} statusFilter - '' = all
 * @param {string} locationFilter - '' = all
 * @returns {number}
 */
const countPersonas = (personas, statusFilter, locationFilter) => {
    const arr = Array.isArray(personas) ? personas : [];
    return arr.filter((p) => {
        if (statusFilter && p?.Status !== statusFilter) return false;
        if (!recordInLocation(p, locationFilter)) return false;
        return true;
    }).length;
};

/**
 * @extends {BaseWidget}
 */
export class PersonasTotalWidget extends BaseWidget {

    getName() { return 'Totaal aantal medewerkers'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    countsAllPersonas() { return true; }
    getIcon() { return <GroupsOutlinedIcon />; }

    getControls() {
        return {
            'statusFilter': new SelectControl({
                label: 'Status',
                description: 'Filter op status. Leeg = alle statussen.',
                defaultValue: '',
                options: {
                    // Sleutels, volgorde en labels uit de canonieke lijst. De
                    // korte schrijfwijze van 'outflow' is hier historisch.
                    choices: [
                        { value: '', label: 'Alle' },
                        ...PERSONA_STATUS_SHORT_OPTIONS,
                    ],
                    allowSearch: false,
                },
            }),
            'locationFilter': new FirestoreSelectControl({
                label: 'Locatie',
                description: 'Filter op locatie. Leeg = alle locaties.',
                firestore: {
                    collection: 'Locations',
                    valueField: 'docid',
                    labelField: 'Title',
                    orderBy: 'Position',
                    includeAll: true,
                },
            }),
            'label': new TextControl({
                label: 'Label',
                description: 'Tekst die onder het getal wordt getoond.',
                defaultValue: 'Medewerkers',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} [props.statusFilter]
 * @param {string} [props.locationFilter]
 * @param {string} [props.label]
 * @returns {JSX.Element}
 */
const Renderer = ({ statusFilter, locationFilter, label }) => {
    const { personasOn } = useSettings();
    const isTemplate = isTemplateMode();
    const personas = usePersonaLocations();

    const total = useMemo(
        () => countPersonas(personas, statusFilter, locationFilter),
        [personas, statusFilter, locationFilter]
    );

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (personasOn !== 'flex') {
        return <ModulePlaceholder mode="disabled" moduleLabel="Medewerkers" />;
    }

    return (
        <MetricVisualization
            value={total}
            label={label || 'Medewerkers'}
        />
    );
};

export { countPersonas };
